import * as portCallActions from 'domain/PortCall/actions'
import React, { useCallback, useEffect } from 'react'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import merge from 'lodash/merge'
import paths from 'constants/paths'
import { POLLING_INTERVAL } from 'global/settings'
import propTypes from 'global/propTypes'
import PropTypes from 'prop-types'
import { selectors } from 'domain'
import useLoadingPageStatus from 'hooks/useLoadingPageStatus'
import { useParams } from 'react-router-dom'
import withLayout from 'components/layout'

import PortCall from './PortCall'

const PortCallContainer = ({
  baplieRestowHistory,
  baplieRestowHistoryError,
  baplieRestowDownloadError,
  baplieUploadError,
  comments,
  containerMovesProgress,
  containerNotes,
  departureAndCompletionTime,
  downloadPortCallReport,
  downloadTerminalPerformanceReport,
  draftSurvey,
  draftSurveySaveError,
  gantryMovesSummary,
  gearboxLocations,
  general,
  isCommentsAndLogbookRemarksTabLoading,
  isPostingComment,
  isRequestingBaplieRestowHistory,
  isRequestingComments,
  isRequestingContainerMovesProgress,
  isRequestingDepartureAndCompletionTime,
  isRequestingDraftSurvey,
  isRequestingGantryMovesSummary,
  isRequestingGearboxLocations,
  isRequestingGeneral,
  isRequestingLogbookRemarks,
  isRequestingContainerNotes,
  isRequestingMilestones,
  isRequestingProductivity,
  isRequestingReportsSummary,
  isRequestingShipOverview,
  isRequestingShipOverviewTab,
  isRequestingSummaryTab,
  isDownloadingBaplieRestow,
  isUploadingBaplieRestow,
  isSavingDraftSurvey,
  logbookRemarks,
  milestones,
  numberOfComments,
  numberOfLogbookRemarks,
  numberOfContainerNotes,
  portCallFeedbackById,
  postPortCallComment,
  productivity,
  reportsSummary,
  requestDepartureEstimation,
  requestPortCallBaplieRestowHistory,
  requestPortCallCommentsTab,
  requestPortCallGeneral,
  requestPortCallProductivity,
  requestPortCallShipOverviewTab,
  requestPortCallSummaryTab,
  savePortCallDraftSurvey,
  sendPortCallFeedback,
  setSavePortCallDraftSurveyError,
  setUnlockPortCallDeviceBayError,
  setUploadingPortCallBaplieRestow,
  downloadPortCallBaplieRestow,
  portCallBaplieRestowUploadFailed,
  shipOverview,
  unlockDeviceBayError,
  unlockingDeviceId,
  unlockPortCallDeviceBay,
  uploadPortCallBaplieRestow,
  userRole,
}) => {
  const { imoNumber, portCallId, tab } = useParams()

  useEffect(() => {
    // Reset the upload state when entering a port call page because the browser
    // may have been closed or left while an upload was in progress, preventing
    // the normal success/error callback from updating the Redux state.
    setUploadingPortCallBaplieRestow(false)
  }, [portCallId, setUploadingPortCallBaplieRestow])

  const isPortCallFinished = general && general.isPortCallFinished

  const requestGeneralData = useCallback(() => {
    requestPortCallGeneral(portCallId)
    requestDepartureEstimation(portCallId)
    requestPortCallCommentsTab(portCallId)
  }, [
    portCallId,
    requestPortCallGeneral,
    requestDepartureEstimation,
    requestPortCallCommentsTab,
  ])

  const requestTabData = useCallback(() => {
    switch (tab) {
      case paths.portCall.summary:
        return requestPortCallSummaryTab(portCallId)

      case paths.portCall.productivity:
        return requestPortCallProductivity(portCallId)

      case paths.portCall.shipOverview:
        requestPortCallShipOverviewTab(portCallId)

        return requestPortCallBaplieRestowHistory(portCallId)

      case paths.portCall.comments:
        return requestPortCallCommentsTab(portCallId)

      default:
        return
    }
  }, [
    portCallId,
    requestPortCallBaplieRestowHistory,
    requestPortCallCommentsTab,
    requestPortCallProductivity,
    requestPortCallShipOverviewTab,
    requestPortCallSummaryTab,
    tab,
  ])

  const handlePostComment = (comment, resetForm) =>
    postPortCallComment({
      comment,
      portCallId,
      resetForm,
    })

  const handleUploadBaplie = ({ file, filename, onError, onSuccess, type }) =>
    uploadPortCallBaplieRestow({
      file,
      filename,
      onError,
      onSuccess,
      portCallId,
      type,
    })

  const handleDownloadBaplieRestow = ({
    fileId,
    filename,
    onError,
    onSuccess,
    type,
  }) =>
    downloadPortCallBaplieRestow({ fileId, filename, onError, onSuccess, type })

  useEffect(() => {
    requestTabData()
  }, [requestTabData])

  useEffect(() => {
    requestGeneralData()
  }, [requestGeneralData])

  useEffect(() => {
    if (!isPortCallFinished) {
      const requestInterval = setInterval(() => {
        requestGeneralData()
      }, POLLING_INTERVAL)

      return () => clearInterval(requestInterval)
    }
  }, [isPortCallFinished, requestGeneralData])

  useEffect(() => {
    if (!isPortCallFinished) {
      const requestInterval = setInterval(() => {
        requestTabData()
      }, POLLING_INTERVAL)

      return () => clearInterval(requestInterval)
    }
  }, [isPortCallFinished, requestTabData])

  // Tabs displays the number of comments and logbook remarks so must wait for data to load.
  const shouldShowTabsLoading = useLoadingPageStatus(
    isCommentsAndLogbookRemarksTabLoading
  )

  const downloadReportParams = {
    imoNumber,
    portCallId,
    portName: general && general.portName,
    shipName: general && general.shipName,
  }

  const downloadPortCallReportWithParams = () => {
    downloadPortCallReport({
      createdAt: reportsSummary.portCallReport.createdAt,
      ...downloadReportParams,
    })
  }

  const downloadTerminalPerformanceReportWithParams = () => {
    downloadTerminalPerformanceReport({
      createdAt: reportsSummary.terminalPerformanceReport.createdAt,
      ...downloadReportParams,
    })
  }

  const shouldShowGeneralLoadingSkeleton = useLoadingPageStatus(
    isRequestingGeneral || isRequestingDepartureAndCompletionTime
  )

  const shouldShowProductivityLoading = useLoadingPageStatus(
    isRequestingProductivity
  )
  const shouldShowDraftSurveyLoading = useLoadingPageStatus(
    isRequestingDraftSurvey
  )

  const shouldShowSummaryTabLoadingSkeleton = useLoadingPageStatus(
    isRequestingSummaryTab
  )

  const shouldShowShipOverviewTabLoadingSkeleton = useLoadingPageStatus(
    isRequestingShipOverviewTab
  )

  const handleSendFeedback = feedback =>
    sendPortCallFeedback({
      feedback,
      isPortCallClosed: isPortCallFinished,
      portCallId,
    })

  const answeredFeedbackIds = portCallFeedbackById[portCallId]
    ? portCallFeedbackById[portCallId]
    : []

  const handleSavePortCallDraftSurvey = ({ payload, onSuccess, type }) =>
    savePortCallDraftSurvey({
      onSuccess,
      payload: {
        createdAt: new Date().toISOString(),
        imoNumber,
        ...payload,
        portCallId,
      },
      type,
    })

  const handleUnlockDeviceBay = (deviceId, bayNumber, onSettled) =>
    unlockPortCallDeviceBay({ bayNumber, deviceId, onSettled, portCallId })

  return (
    <PortCall
      answeredFeedbackIds={answeredFeedbackIds}
      baplieRestowDownloadError={baplieRestowDownloadError}
      baplieRestowHistory={baplieRestowHistory}
      baplieRestowHistoryError={baplieRestowHistoryError}
      baplieUploadError={baplieUploadError}
      comments={comments}
      containerMovesProgress={containerMovesProgress}
      containerNotes={containerNotes}
      departureAndCompletionTime={departureAndCompletionTime}
      downloadPortCallReport={downloadPortCallReportWithParams}
      draftSurvey={draftSurvey}
      draftSurveySaveError={draftSurveySaveError}
      gantryMovesSummary={gantryMovesSummary}
      gearboxLocations={gearboxLocations}
      general={general}
      handlePostComment={handlePostComment}
      handleSendFeedback={handleSendFeedback}
      isDownloadingBaplieRestow={isDownloadingBaplieRestow}
      isPostingComment={isPostingComment}
      isRequestingBaplieRestowHistory={isRequestingBaplieRestowHistory}
      isRequestingComments={isRequestingComments}
      isRequestingContainerMovesProgress={isRequestingContainerMovesProgress}
      isRequestingContainerNotes={isRequestingContainerNotes}
      isRequestingDraftSurvey={isRequestingDraftSurvey}
      isRequestingGantryMovesSummary={isRequestingGantryMovesSummary}
      isRequestingGearboxLocations={isRequestingGearboxLocations}
      isRequestingGeneral={isRequestingGeneral}
      isRequestingLogbookRemarks={isRequestingLogbookRemarks}
      isRequestingMilestones={isRequestingMilestones}
      isRequestingReportsSummary={isRequestingReportsSummary}
      isRequestingShipOverview={isRequestingShipOverview}
      isSavingDraftSurvey={isSavingDraftSurvey}
      isUploadingBaplieRestow={isUploadingBaplieRestow}
      logbookRemarks={logbookRemarks}
      milestones={milestones}
      numberOfComments={numberOfComments}
      numberOfContainerNotes={numberOfContainerNotes}
      numberOfLogbookRemarks={numberOfLogbookRemarks}
      onDownloadBaplieRestow={handleDownloadBaplieRestow}
      onSavePortCallDraftSurvey={handleSavePortCallDraftSurvey}
      onUnlockDeviceBay={handleUnlockDeviceBay}
      onUploadBaplieRestow={handleUploadBaplie}
      portCallBaplieRestowUploadFailed={portCallBaplieRestowUploadFailed}
      productivity={productivity}
      reportsSummary={reportsSummary}
      setSavePortCallDraftSurveyError={setSavePortCallDraftSurveyError}
      setUnlockDeviceBayError={setUnlockPortCallDeviceBayError}
      shipOverview={shipOverview}
      shouldShowDraftSurveyLoading={shouldShowDraftSurveyLoading}
      shouldShowGeneralLoadingSkeleton={shouldShowGeneralLoadingSkeleton}
      shouldShowProductivityLoading={shouldShowProductivityLoading}
      shouldShowSummaryTabLoadingSkeleton={shouldShowSummaryTabLoadingSkeleton}
      shouldShowTabsLoading={shouldShowTabsLoading}
      tab={tab}
      unlockDeviceBayError={unlockDeviceBayError}
      unlockingDeviceId={unlockingDeviceId}
      userRole={userRole}
      downloadTerminalPerformanceReport={
        downloadTerminalPerformanceReportWithParams
      }
      isRequestingDepartureAndCompletionTime={
        isRequestingDepartureAndCompletionTime
      }
      shouldShowShipOverviewTabLoadingSkeleton={
        shouldShowShipOverviewTabLoadingSkeleton
      }
    />
  )
}

PortCallContainer.defaultProps = {
  baplieRestowDownloadError: null,
  baplieRestowHistory: [],
  baplieUploadError: null,
  comments: null,
  containerMovesProgress: null,
  draftSurvey: {
    arrivalDrafts: null,
    arrivalWaterDensity: null,
    departureDrafts: null,
    departureWaterDensity: null,
  },
  general: null,
  productivity: null,
  reportsSummary: null,
  shipOverview: null,
  unlockDeviceBayError: null,
  unlockingDeviceId: null,
}

PortCallContainer.propTypes = {
  baplieRestowDownloadError: PropTypes.string,
  baplieRestowHistory: PropTypes.array,
  baplieRestowHistoryError: PropTypes.bool.isRequired,
  baplieUploadError: PropTypes.string,
  comments: PropTypes.arrayOf(propTypes.portCallComment),
  containerMovesProgress: propTypes.containerMovesProgress,
  containerNotes: propTypes.containerNotes.isRequired,
  departureAndCompletionTime: propTypes.departureAndCompletionTime.isRequired,
  downloadPortCallBaplieRestow: PropTypes.func.isRequired,
  downloadPortCallReport: PropTypes.func.isRequired,
  downloadTerminalPerformanceReport: PropTypes.func.isRequired,
  draftSurvey: propTypes.draftSurvey,
  draftSurveySaveError: PropTypes.string,
  gantryMovesSummary: propTypes.gantryMovesSummary.isRequired,
  gearboxLocations: PropTypes.arrayOf(PropTypes.string).isRequired,
  general: propTypes.portCallGeneral,
  isCommentsAndLogbookRemarksTabLoading: PropTypes.bool.isRequired,
  isDownloadingBaplieRestow: PropTypes.bool.isRequired,
  isPostingComment: PropTypes.bool.isRequired,
  isRequestingBaplieRestowHistory: PropTypes.bool.isRequired,
  isRequestingComments: PropTypes.bool.isRequired,
  isRequestingContainerMovesProgress: PropTypes.bool.isRequired,
  isRequestingContainerNotes: PropTypes.bool.isRequired,
  isRequestingDepartureAndCompletionTime: PropTypes.bool.isRequired,
  isRequestingDraftSurvey: PropTypes.bool.isRequired,
  isRequestingGantryMovesSummary: PropTypes.bool.isRequired,
  isRequestingGearboxLocations: PropTypes.bool.isRequired,
  isRequestingGeneral: PropTypes.bool.isRequired,
  isRequestingLogbookRemarks: PropTypes.bool.isRequired,
  isRequestingMilestones: PropTypes.bool.isRequired,
  isRequestingProductivity: PropTypes.bool.isRequired,
  isRequestingReportsSummary: PropTypes.bool.isRequired,
  isRequestingShipOverview: PropTypes.bool.isRequired,
  isRequestingShipOverviewTab: PropTypes.bool.isRequired,
  isRequestingSummaryTab: PropTypes.bool.isRequired,
  isSavingDraftSurvey: PropTypes.bool.isRequired,
  isUploadingBaplieRestow: PropTypes.bool.isRequired,
  logbookRemarks: propTypes.logbookRemarks.isRequired,
  milestones: propTypes.milestones.isRequired,
  numberOfComments: PropTypes.number.isRequired,
  numberOfContainerNotes: PropTypes.number.isRequired,
  numberOfLogbookRemarks: PropTypes.number.isRequired,
  portCallBaplieRestowUploadFailed: PropTypes.func.isRequired,
  portCallFeedbackById: PropTypes.object.isRequired,
  postPortCallComment: PropTypes.func.isRequired,
  productivity: propTypes.portCallProductivity,
  reportsSummary: propTypes.reports,
  requestDepartureEstimation: PropTypes.func.isRequired,
  requestPortCallBaplieRestowHistory: PropTypes.func.isRequired,
  requestPortCallCommentsTab: PropTypes.func.isRequired,
  requestPortCallGeneral: PropTypes.func.isRequired,
  requestPortCallProductivity: PropTypes.func.isRequired,
  requestPortCallShipOverviewTab: PropTypes.func.isRequired,
  requestPortCallSummaryTab: PropTypes.func.isRequired,
  savePortCallDraftSurvey: PropTypes.func.isRequired,
  sendPortCallFeedback: PropTypes.func.isRequired,
  setSavePortCallDraftSurveyError: PropTypes.func,
  setUnlockPortCallDeviceBayError: PropTypes.func.isRequired,
  setUploadingPortCallBaplieRestow: PropTypes.func.isRequired,
  shipOverview: propTypes.shipOverview,
  unlockDeviceBayError: PropTypes.string,
  unlockPortCallDeviceBay: PropTypes.func.isRequired,
  unlockingDeviceId: PropTypes.string,
  uploadPortCallBaplieRestow: PropTypes.func.isRequired,
  userRole: propTypes.userRole.isRequired,
}

const mapStateToProps = state => ({
  baplieRestowDownloadError: selectors.baplieRestowDownloadErrorSelector(state),
  baplieRestowHistory: selectors.baplieRestowHistorySelector(state),
  baplieRestowHistoryError: selectors.baplieRestowHistoryErrorSelector(state),
  baplieUploadError: selectors.baplieRestowUploadErrorSelector(state),
  comments: selectors.portCallCommentsSelector(state),
  containerMovesProgress: selectors.containerMovesProgressSelector(state),
  containerNotes: selectors.containerNotesSelector(state),
  departureAndCompletionTime:
    selectors.departureAndCompletionTimeSelector(state),
  draftSurvey: selectors.portCallDraftSurveySelector(state),
  draftSurveySaveError: selectors.draftSurveySaveErrorSelector(state),
  gantryMovesSummary: selectors.gantryMovesSummarySelector(state),
  gearboxLocations: selectors.gearboxLocationsSelector(state),
  general: selectors.portCallGeneralSelector(state),
  isCommentsAndLogbookRemarksTabLoading:
    selectors.isCommentsAndLogbookRemarksTabLoadingSelector(state),
  isDownloadingBaplieRestow: selectors.isDownloadingBaplieRestowSelector(state),
  isPostingComment: selectors.isPostingPortCallCommentSelector(state),
  isRequestingBaplieRestowHistory:
    selectors.isRequestingBaplieRestowHistorySelector(state),
  isRequestingComments: selectors.isRequestingPortCallCommentsSelector(state),
  isRequestingContainerMovesProgress:
    selectors.isRequestingContainerMovesProgressSelector(state),
  isRequestingContainerNotes:
    selectors.isRequestingContainerNotesSelector(state),
  isRequestingDepartureAndCompletionTime:
    selectors.isRequestingDepartureAndCompletionTimeSelector(state),
  isRequestingDraftSurvey: selectors.isRequestingDraftSurveySelector(state),
  isRequestingGantryMovesSummary:
    selectors.isRequestingGantryMovesSummarySelector(state),
  isRequestingGearboxLocations:
    selectors.isRequestingGearboxLocationsSelector(state),
  isRequestingGeneral: selectors.isRequestingGeneralSelector(state),
  isRequestingLogbookRemarks:
    selectors.isRequestingLogbookRemarksSelector(state),
  isRequestingMilestones: selectors.isRequestingMilestonesSelector(state),
  isRequestingProductivity: selectors.isRequestingProductivitySelector(state),
  isRequestingReportsSummary:
    selectors.isRequestingReportsSummarySelector(state),
  isRequestingShipOverview: selectors.isRequestingShipOverviewSelector(state),
  isRequestingShipOverviewTab:
    selectors.isRequestingShipOverviewTabSelector(state),
  isRequestingSummaryTab: selectors.isSummaryTabLoadingSelector(state),
  isSavingDraftSurvey: selectors.isSavingDraftSurveySelector(state),
  isUploadingBaplieRestow: selectors.isUploadingBaplieRestowSelector(state),
  logbookRemarks: selectors.logbookRemarksSelector(state),
  milestones: selectors.milestonesSelector(state),
  numberOfComments: selectors.numberOfCommentsSelector(state),
  numberOfContainerNotes: selectors.numberOfContainerNotesSelector(state),
  numberOfLogbookRemarks: selectors.numberOfLogbookRemarksSelector(state),
  portCallFeedbackById: selectors.portCallFeedbackByIdSelector(state),
  productivity: selectors.portCallProductivitySelector(state),
  reportsSummary: selectors.portCallReportsSummarySelector(state),
  shipOverview: selectors.shipOverviewSelector(state),
  unlockDeviceBayError: selectors.unlockDeviceBayErrorSelector(state),
  unlockingDeviceId: selectors.unlockingDeviceIdSelector(state),
  userRole: selectors.userRoleSelector(state),
})

const mapDispatchToProps = dispatch =>
  bindActionCreators(merge({}, portCallActions), dispatch)

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withLayout(PortCallContainer))
